import React from 'react';
import { Parse } from 'parse';
import { MediaItem } from '../experiences/LifeObjectRenderer';

function Comment({ comment, comments, onReply, onLike, busy, depth = 0 }) {
  const [expanded, setExpanded] = React.useState(false);
  const replies = comments.filter(
    (item) => item.parentCommentId === comment.id
  );
  return (
    <div
      className="lt-comment"
      style={{ marginLeft: depth ? Math.min(depth, 3) * 8 : 0 }}
    >
      <div className="lt-comment-author">
        <span className="lt-avatar">
          {comment.author?.name?.charAt(0)?.toUpperCase() || '?'}
        </span>
        <strong>{comment.author?.name || 'Name unavailable'}</strong>
        <time>
          {comment.createdAt
            ? new Date(comment.createdAt).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
              })
            : ''}
        </time>
      </div>
      <p>{comment.body}</p>
      {comment.content?.map((item, index) => (
        <MediaItem key={index} item={item} />
      ))}
      <div className="lt-comment-actions">
        <button
          disabled={busy}
          aria-label={comment.liked ? 'Unlike comment' : 'Like comment'}
          aria-pressed={comment.liked}
          onClick={() => onLike(comment)}
        >
          {comment.liked ? '♥' : '♡'} {comment.likeCount || 0}
        </button>
        <button onClick={() => onReply(comment)}>Reply</button>
        {replies.length > 0 && (
          <button
            aria-label={expanded ? 'Collapse replies' : 'Expand replies'}
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? '⌃' : '⌄'} {replies.length}
          </button>
        )}
      </div>
      {expanded &&
        replies.map((reply) => (
          <Comment
            key={reply.id}
            {...{ comment: reply, comments, onReply, onLike, busy }}
            depth={depth + 1}
          />
        ))}
    </div>
  );
}

export default function StudioConversation({ lifeObjectId }) {
  const [data, setData] = React.useState(null);
  const [comments, setComments] = React.useState([]);
  const [body, setBody] = React.useState('');
  const [reply, setReply] = React.useState(null);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [retry, setRetry] = React.useState(0);
  React.useEffect(() => {
    let active = true;
    setError('');
    setData(null);
    setComments([]);
    Parse.Cloud.run('getLifeObjectConversation', {
      lifeObjectId,
      page: 1,
      limit: 50,
    })
      .then((result) => {
        if (active) {
          setData(result);
          setComments(result.comments || []);
        }
      })
      .catch(
        () => active && setError('The conversation could not load. Try again.')
      );
    return () => {
      active = false;
    };
  }, [lifeObjectId, retry]);
  const more = async () => {
    setBusy(true);
    try {
      const result = await Parse.Cloud.run('getLifeObjectConversation', {
        lifeObjectId,
        page: (data.page || 1) + 1,
        limit: 50,
      });
      setData(result);
      setComments((current) => {
        const byId = new Map(current.map((c) => [c.id, c]));
        (result.comments || []).forEach((c) => byId.set(c.id, c));
        return [...byId.values()];
      });
    } catch {
      setError('More replies could not load. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  const send = async (event) => {
    event.preventDefault();
    if (!body.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      const comment = await Parse.Cloud.run('addLifeObjectComment', {
        lifeObjectId,
        body: body.trim(),
        ...(reply ? { parentCommentId: reply.id } : {}),
      });
      setComments((current) => [...current, comment]);
      setBody('');
      setReply(null);
    } catch {
      setError(
        'Your response wasn’t sent. Your text is still here—please try again.'
      );
    } finally {
      setBusy(false);
    }
  };
  const like = async (comment) => {
    setBusy(true);
    setError('');
    try {
      await Parse.Cloud.run('setLifeObjectCommentLike', {
        lifeObjectId,
        commentId: comment.id,
        active: !comment.liked,
      });
      setComments((current) =>
        current.map((c) =>
          c.id === comment.id
            ? {
                ...c,
                liked: !c.liked,
                likeCount: Math.max(0, (c.likeCount || 0) + (c.liked ? -1 : 1)),
              }
            : c
        )
      );
    } catch {
      setError('The reaction could not be saved. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  const loadedIds = new Set(comments.map((c) => c.id));
  return (
    <div className="lt-conversation">
      {error && (
        <div role="alert" className="lt-conversation-error">
          {error}
          {!data && <button onClick={() => setRetry(retry + 1)}>Retry</button>}
        </div>
      )}
      {!data && !error && <p>Loading the conversation…</p>}
      {data && (
        <>
          <div className="lt-conversation-people">
            <span className="lt-section-eyebrow">
              {data.identity?.label || 'PARTICIPANTS'}
            </span>
            <div>
              {data.identity?.participants?.map((person) => (
                <span key={person.id}>
                  <span className="lt-avatar">
                    {person.name?.charAt(0)?.toUpperCase()}
                  </span>
                  {person.name}
                </span>
              ))}
            </div>
          </div>
          {!comments.length && (
            <div className="lt-conversation-empty">
              <h3>Make the connection.</h3>
              <p>No replies yet. This is where the conversation continues.</p>
            </div>
          )}
          {comments
            .filter(
              (c) => !c.parentCommentId || !loadedIds.has(c.parentCommentId)
            )
            .map((comment) => (
              <Comment
                key={comment.id}
                {...{
                  comment,
                  comments,
                  onReply: setReply,
                  onLike: like,
                  busy,
                }}
              />
            ))}
          {data.hasMore && (
            <button
              className="lt-detail-secondary"
              disabled={busy}
              onClick={more}
            >
              Load more responses
            </button>
          )}
          <form onSubmit={send} className="lt-comment-compose">
            {reply && (
              <div>
                Replying to {reply.author?.name}
                <button
                  type="button"
                  onClick={() => setReply(null)}
                  aria-label="Cancel reply"
                >
                  ×
                </button>
              </div>
            )}
            <label htmlFor="lt-comment-body">Add to the conversation</label>
            <textarea
              id="lt-comment-body"
              value={body}
              maxLength={2000}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
            />
            <button
              className="lt-button"
              disabled={busy || !body.trim()}
              type="submit"
            >
              {busy ? 'Saving…' : 'Send response'}
            </button>
          </form>
          <p className="lt-conversation-privacy">
            Discovery is private. Only intentional responses are shared here.
          </p>
        </>
      )}
    </div>
  );
}
